import { cn } from "@/lib/utils";
import { type ReactNode } from "react";

type TaglineProps = {
  /** Render as a different heading element. */
  as?: "h1" | "h2" | "h3" | "p";
  /** Additional classes applied to the root element. */
  className?: string;
  /**
   * Optional children. When provided, the component only applies the shared
   * brass-accent typography and spacing, leaving the words up to you.
   * Use <span className="text-brass">ease</span> inside children for accents.
   */
  children?: ReactNode;
  /**
   * Simplified text mode. Provide a plain string and an array of words to
   * highlight with the brass accent. The rest of the text uses the default
   * foreground color.
   */
  text?: string;
  /** Words inside `text` that should receive the brass accent. Defaults to ["ease"]. */
  brassWords?: string[];
};

/**
 * Shared site tagline component. Keeps the brass-accent typography,
 * text balancing, and responsive spacing consistent wherever the tagline
 * appears.
 */
export function Tagline({
  as: Component = "h1",
  className,
  children,
  text = "Find and audit your easements. Buy or build with ease.",
  brassWords = ["ease"],
}: TaglineProps) {
  return (
    <Component
      className={cn(
        "mx-auto max-w-3xl text-balance font-display text-4xl font-semibold leading-[1.12] tracking-tight sm:text-6xl sm:leading-[1.05]",
        className,
      )}
    >
      {children ?? renderBrassText(text, brassWords)}
    </Component>
  );
}

function renderBrassText(text: string, brassWords: string[]) {
  const words = text.split(/(\s+)/);
  return words.map((word, index) => {
    const clean = word.replace(/[^\w]/g, "").toLowerCase();
    const isBrass = brassWords.some((bw) => bw.toLowerCase() === clean);
    if (isBrass) {
      return (
        <span key={index} className="text-brass">
          {word}
        </span>
      );
    }
    return <span key={index}>{word}</span>;
  });
}
